import * as React from "react";

import { faCloud, faHdd } from "@fortawesome/free-solid-svg-icons";
import { Box, Divider } from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import * as Sentry from "@sentry/nextjs";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { MyOrgContext } from "context/myorgcontext";
import { UserContext } from "context/usercontext";
import { Form } from "src/common/components/Form";
import Loader from "src/common/components/Loader";
import { FontAwesomeIconWithTheme } from "src/common/components/ui/IconWrapper";
import useBridgeSWR from "src/common/hooks/BridgeSWRClient";
import {
  CreateTemplateGroupDTO,
  useCreateTemplateGroup,
} from "src/domain/administrative/resource-management/features/templates/api/createTemplateGroup";
import { GroupInput } from "src/domain/administrative/resource-management/features/templates/components/GroupInput";
import { useUpdateDisk } from "src/domain/core/disks";
import { useCreateDisk } from "src/domain/core/disks/api/createDisk";
import { CreateTemplateDTO, TemplateConfig, TemplateNameInput, useCreateTemplate } from "src/domain/core/templates";
import { CreateWorkflowDTO, useCreateWorkflow } from "src/domain/core/workflow/api/createWorkflow";
import { AdvancedSettings } from "src/domain/core/workflow/components/create/AdvancedSettings";
import { BasicInfoInput } from "src/domain/core/workflow/components/create/BasicInfoInput";
import { CredentialsInput } from "src/domain/core/workflow/components/create/CredentialsInput";
import { DiskInfoInput, TemplateDiskInfo } from "src/domain/core/workflow/components/create/DiskInput";
import { ExpandableSettings } from "src/domain/core/workflow/components/create/ExpandableSettings";
import { NodeInput } from "src/domain/core/workflow/components/create/NodeInput";
import { SchedulerInput } from "src/domain/core/workflow/components/create/SchedulerInput";
import { TagsInput } from "src/domain/core/workflow/components/create/TagsInput";
import { endpoint } from "src/lib/bridgeClient";
import { ALLOWED_MAX_DISK_SIZE } from "src/lib/const";

const ButtonGroup = styled("div")(() => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
}));

const SubHeading = styled("div")(() => ({
  marginBottom: "25px",
  svg: {
    marginRight: "5px",
  },
}));

const LoadingContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  // This height allows the "loading" modal to approximate the height of the full form,
  // which makes the transition from loading-to-form smoother.
  height: "85vh",
}));

const loadingState = (message: string) => {
  return (
    <LoadingContainer>
      <div style={{}} key="loader">
        <Loader />
      </div>
      <span key="loader-message">{message}</span>
    </LoadingContainer>
  );
};

export type CreateFormInput = {
  workflow_owner_password_confirm: string;
  newDiskName: string;
  newDiskSize: number;
  existingDiskName: string;
  existingDiskSize: number;
  name: string;
  access_from: string;
  compute_node_instance_type: string;
  initial_queue_size: number;
  aws_keypair_name: string;
  maintain_initial_size: boolean;
  head_node_instance_type: string;
  max_queue_size: number;
  stack_id: number;
  workflow_owner_password: string;
  workflow_owner_username: string;
  volume_name: string;
  volume_id?: string;
  volume_size: number;
  hostname: string;
  tags: string;
  scheduler_id: number;
  is_gpu: boolean;
  templateName: string;
  sync_workflow_hostname: boolean;
  groups?: string[];
  blueprint?: string;
};

function determineWorkflowUsername(preferredUsername, email) {
  if (preferredUsername) {
    return preferredUsername;
  }

  // Regex searches the beginning of the email up until the first character
  // that is not a letter, number, or underscore.
  const nameFromEmail = email.match(/^[a-zA-Z0-9_]+/);
  if (nameFromEmail) {
    const workflowUsername = nameFromEmail[0];
    return workflowUsername;
  }

  Sentry.captureMessage(
    `cannot derive a valid workflow username for user. Preferred username isn't set and email "${email}" cannot be converted into a username.`
  );
  return;
}

const validationSchema = (
  availableDisks: Disk[],
  inUseDisks: Disk[],
  templates: Template[],
  templateName: string,
  existingDiskSize: number,
  requirePassword: boolean,
  isPowerUser: boolean,
  saveTemplate: boolean,
  isCreateTemplate: boolean
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let validation: Record<string, any> = {
    name: Yup.string()
      .required("Workflow name is required")
      .matches(/^[a-zA-Z](.*)+$/, "Must start with a letter")
      .matches(/^[a-zA-Z0-9]+$/, "Can only contain letters or numbers")
      .min(4, "Must be at least 4 characters")
      .max(40, "Must be 40 characters or less"),
    scheduler_id: Yup.number().required("Scheduler is required"),
    head_node_instance_type: Yup.string().required("Head node is required"),
    compute_node_instance_type: Yup.string().required("Compute node is required"),
    tags: Yup.string().test("is-json", "Provided tags are not valid JSON", (value) => {
      try {
        // Try to parse JSON to ensure proper format, if it fails will return false in catch clause
        if (value) {
          JSON.parse(value);
        }
        return true;
      } catch (error) {
        return false;
      }
    }),
  };

  if (!isCreateTemplate) {
    validation = {
      ...validation,
      hostname: Yup.string()
        .test("starting-char", "Must start with a letter or number", (hostname) => {
          if (hostname) {
            const match = hostname.match(/^[a-zA-Z0-9](.*)+$/);
            if (!match) {
              return false;
            }
          }
          return true;
        })
        .test("valid-char", "Can only contain letters, numbers, and hyphen", (hostname) => {
          if (hostname) {
            const match = hostname.match(/^[a-zA-Z0-9-]+$/);
            if (!match) {
              return false;
            }
          }
          return true;
        }),
      access_from: Yup.string().when("$usePublicIPs", (usePublicIPs, schema) => {
        if (usePublicIPs) {
          return schema.matches(
            /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]).){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/(3[0-2]|[1-2][0-9]|[0-9]))$/,
            "IP address format needs to x.x.x.x/x"
          );
        }
      }),
      workflow_owner_username: Yup.string()
        .required("Username is required. Please contact an administrator for assistance.")
        .matches(
          /^[a-zA-Z0-9_.]+$/,
          "Username must consist of only letters, numbers, underscores, and periods. Please contact an administrator for assistance."
        ),
      // Disk Input
      newDiskName: Yup.string().when("$newDiskDisabled", (newDiskDisabled, schema) => {
        if (!newDiskDisabled) {
          return schema
            .required("Disk name is required")
            .test("availableDiskCheck", "Disk already exists with this name", (item: string) => {
              const lowercaseDisks = availableDisks?.map((disk) => disk.name.toLowerCase());
              return !lowercaseDisks.includes(item.toLowerCase());
            })
            .test("inUseDiskCheck", "In-use disk already exists with this name", (item: string) => {
              const lowercaseDisks = inUseDisks?.map((disk) => disk.name.toLowerCase());
              return !lowercaseDisks.includes(item.toLowerCase());
            });
        }
        return schema.notRequired();
      }),
      newDiskSize: Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .when("$newDiskDisabled", (newDiskDisabled, schema) => {
          if (!newDiskDisabled) {
            return schema
              .transform((value) => (isNaN(value) ? undefined : value))
              .required("Enter a valid disk size")
              .min(5, "Must be greater than or equal to 5")
              .max(ALLOWED_MAX_DISK_SIZE, `Must be less than or equal to ${ALLOWED_MAX_DISK_SIZE}`);
          }
          return schema.notRequired();
        }),
      existingDiskName: Yup.string().when("$existingDiskDisabled", (existingDiskDisabled, schema) => {
        if (!existingDiskDisabled) {
          return schema.required();
        }
        return schema.notRequired();
      }),
      existingDiskSize: Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .when("$existingDiskDisabled", (existingDiskDisabled, schema) => {
          if (!existingDiskDisabled) {
            return schema
              .transform((value) => (isNaN(value) ? undefined : value))
              .required("Enter a valid disk size")
              .min(existingDiskSize, `Must be greater than or equal to ${existingDiskSize}`)
              .max(ALLOWED_MAX_DISK_SIZE, `Must be less than or equal to ${ALLOWED_MAX_DISK_SIZE}`);
          }
          return schema.notRequired();
        }),
    };
  }
  if (isPowerUser && !isCreateTemplate) {
    validation = {
      ...validation,
      initial_queue_size: Yup.number()
        .max(Yup.ref("max_queue_size"), "Initial queue size must be less than or equal to max queue size")
        .transform((value) => (isNaN(value) ? undefined : value))
        .required("Initial queue size is required"),
      max_queue_size: Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required("Max queue size is required"),
      maintain_initial_size: Yup.bool(),
    };
  }
  if (requirePassword && !isCreateTemplate) {
    validation = {
      ...validation,
      workflow_owner_password: Yup.string().required("Password is required").min(6, "Must be at least 6 characters"),
      workflow_owner_password_confirm: Yup.string()
        .required("Confirm Password")
        .oneOf([Yup.ref("workflow_owner_password"), null], "Passwords do not match"),
    };
  }

  if (saveTemplate || isCreateTemplate) {
    validation = {
      ...validation,
      templateName: Yup.string()
        .required("Template name is required")
        .matches(/^[a-zA-Z0-9 ]+$/, "Only numbers, letters, and spaces are allowed")
        .test("unique", "Template Name must be unique", (value) => {
          return !templates.find((template) => {
            return template?.templateName === value && template?.templateName !== templateName;
          });
        }),
    };
  }

  return validation;
};

interface WorkflowsProps {
  blueprintId: number;
  blueprints: WorkflowBlueprintDesc[];
  setBlueprintId: React.Dispatch<React.SetStateAction<number>>;
  nodeData: {
    head_node_instance_types: MachineInstanceTypeDesc[];
    compute_node_instance_types: MachineInstanceTypeDesc[];
    gpu_head_node_instance_types: MachineInstanceTypeDesc[];
    gpu_compute_node_instance_types: MachineInstanceTypeDesc[];
  };
  template?: Template;
  templates: Template[];
  close: () => void;
  disks: Disk[];
  disksError: Error;
  groups?: Group[];
  source?: string;
}

export const LaunchWorkflowForm: React.FC<WorkflowsProps> = ({
  blueprintId,
  blueprints,
  setBlueprintId,
  nodeData,
  groups,
  template,
  templates,
  close,
  disks,
  disksError,
  source,
}) => {
  const user = React.useContext(UserContext);
  const myOrg = React.useContext(MyOrgContext);
  const availableDisks = disks?.filter((disk) => disk?.status === "available");
  const inUseDisks = disks?.filter((disk) => disk?.status === "in-use");

  const templateDiskAvailable = React.useMemo(() => {
    const templateDisk = availableDisks?.find((disk) => disk.id === template?.body?.volumeId);
    return templateDisk && templateDisk.status === "available";
  }, [availableDisks, template]);

  const [submitting, setSubmitting] = React.useState(false);

  const create = useCreateWorkflow();
  const createTemplate = useCreateTemplate(source === "create-template");
  const createDisk = useCreateDisk();
  const updateDisk = useUpdateDisk();
  const [newDiskDisabled, setNewDiskDisabled] = React.useState<boolean>(templateDiskAvailable);
  const [existingDiskDisabled, setExistingDiskDisabled] = React.useState<boolean>(false);
  const [existingDiskSize, setExistingDiskSize] = React.useState<number>(null);
  const [saveTemplate, setSaveTemplate] = React.useState<boolean>(false);
  const [saveDisk, setSaveDisk] = React.useState<boolean>(true);
  const [userSchedulersError, setUserSchedulersError] = React.useState<Error>(null);
  const [blueprintSchedulersError, setBlueprintSchedulersError] = React.useState<Error>(null);

  const createTemplateGroups = useCreateTemplateGroup();

  const createOrgTemplate = async (orgTemplate: CreateTemplateDTO, groupNames: string[]) => {
    const template = await createTemplate(orgTemplate as CreateTemplateDTO);
    if (!groupNames || groupNames?.length === 0) {
      return template;
    }

    //handle creating groups
    const templateId = template.data;
    const groupRows: CreateTemplateGroupDTO[] = groupNames?.map((group) => {
      return {
        workflow_template_id: templateId,
        group_name: group,
      } as CreateTemplateGroupDTO;
    });
    return await createTemplateGroups(groupRows);
  };

  const workflowUsername = React.useMemo(() => determineWorkflowUsername(user.preferred_username, user.email), [user]);

  const { data: blueprint, error: blueprintError } = useBridgeSWR<WorkflowBlueprint>(
    blueprintId ? endpoint.stackByID(blueprintId) : null
  );
  const { data: blueprintActions, error: actionsError } = useBridgeSWR<BlueprintActions>(
    blueprintId ? endpoint.stackActionsByID(blueprintId) : null
  );

  const isCreateTemplate = source === "create-template";
  const launchText = isCreateTemplate ? "Save Template" : "Launch Workflow";

  if (blueprintError || userSchedulersError || blueprintSchedulersError || actionsError) {
    return (
      <div className="centered-disp">
        Unable to load launch workflow form. Please try refreshing page. If problem persists, please reach out to
        support.
      </div>
    );
  }

  if (!blueprint || !blueprintActions) {
    return loadingState("");
  }

  const requirePassword = myOrg?.data.require_workflow_password && !blueprint.is_sso;
  const submit = async (values: CreateFormInput) => {
    setSubmitting(true);

    const resp = await submitRequest(values);
    if (resp?.ok) {
      setSubmitting(false);
      close();
      return;
    }

    setSubmitting(false);
  };

  const findInstanceTypeByName = (instanceList: MachineInstanceTypeDesc[], name: string): string => {
    return instanceList.find((instance) => instance.name === name)?.instance_type;
  };

  const submitRequest = async (values: CreateFormInput) => {
    // If selecting existing disk, check if the size is being updated. If so, trigger an update-disk API call
    if (values.existingDiskName) {
      const disk = availableDisks?.find((disk) => disk.name === values.existingDiskName);
      if (values.existingDiskSize > disk.size) {
        const existingDiskResp = await updateDisk(disk.id, values.existingDiskSize);
        //If request to update disk failed, don't continue any further with creating a new workflow
        if (existingDiskResp?.ok === false) {
          toast.error("Disk size failed to update, exiting workflow creation");
          return existingDiskResp;
        }
      }
    }

    // If selecting new disk, trigger a create-disk API call
    let newDiskVolumeId = "";
    if (values.newDiskName) {
      const createDiskDTO = {
        name: values.newDiskName,
        size: values.newDiskSize,
        username: workflowUsername,
      };

      const newDiskResp = await createDisk(createDiskDTO);
      // If failed to create new disk, don't continue any further with creating a new workflow
      if (newDiskResp?.ok === false) {
        toast.error("Failed to create a new disk, exiting workflow creation");
        return newDiskResp;
      }
      newDiskVolumeId = newDiskResp?.data?.id; //are these question marks needed?
    }

    const headNode = values.is_gpu
      ? findInstanceTypeByName(nodeData?.gpu_head_node_instance_types || [], values.head_node_instance_type)
      : findInstanceTypeByName(nodeData?.head_node_instance_types || [], values.head_node_instance_type);

    const computeNode = values.is_gpu
      ? findInstanceTypeByName(nodeData?.gpu_compute_node_instance_types || [], values.compute_node_instance_type)
      : findInstanceTypeByName(nodeData?.compute_node_instance_types || [], values.compute_node_instance_type);

    const volumeId = newDiskVolumeId || availableDisks?.find((disk) => disk.name === values.existingDiskName)?.id;

    let workflowReq: CreateWorkflowDTO = {
      name: values.name,
      access_from: values.access_from,
      head_node_instance_type: headNode,
      compute_node_instance_type: computeNode,
      stack_id: blueprintId,
      // don't pull this one from values, we don't want the user to be able to modify this
      // with sneaky HTML Editing. That being said, we DO still want to let the user know
      // if there is a problem with their username, so we'll leave validation on for this
      // field. Theoretically, they shouldn't be able to get to this page without a valid
      // name.
      workflow_owner_username: workflowUsername,
      volume_id: volumeId,
      hostname: values.hostname,
      aws_keypair_name: user?.aws_keypair_name || myOrg?.data.aws_keypair_name,
      tags: values.tags ? JSON.parse(values.tags) : {},
      initial_queue_size: null,
      maintain_initial_size: null,
      max_queue_size: null,
      scheduler_id: values.scheduler_id,
      is_gpu: values.is_gpu,
    };

    if (requirePassword) {
      workflowReq = {
        ...workflowReq,
        workflow_owner_password: values.workflow_owner_password,
      };
    }

    if (user?.is_power_user) {
      workflowReq = {
        ...workflowReq,
        initial_queue_size: values.initial_queue_size,
        maintain_initial_size: values.maintain_initial_size,
        max_queue_size: values.max_queue_size,
      };
    }

    if (template) {
      workflowReq = {
        ...workflowReq,
        saved_configuration_name: template.templateName,
        saved_configuration_id: template.id,
      };
    }

    // if not saving workflow template, launch workflow and return
    if (!saveTemplate && !isCreateTemplate) {
      return await create(workflowReq);
    }

    // if not creating an org template, launch the workflow
    if (!isCreateTemplate) {
      await create(workflowReq);
    }

    const templateBlueprint = blueprints.find((blueprint) => {
      return blueprint.name == values.blueprint;
    });

    let templateReq: CreateTemplateDTO = {
      template_name: values.templateName,
      workflow_name: values.name,
      blueprint_id: templateBlueprint?.id ?? blueprintId,
      is_gpu: values.is_gpu,
      head_node_instance_type: headNode,
      compute_node_instance_type: computeNode,
      scheduler_id: values.scheduler_id,
      tags: values.tags || "",
    };

    if (saveDisk) {
      templateReq = {
        ...templateReq,
        volume_id: volumeId,
      };
    }

    if (isCreateTemplate) {
      return createOrgTemplate(templateReq, values.groups);
    }

    return await createTemplate(templateReq);
  };

  const validation = validationSchema(
    availableDisks,
    inUseDisks,
    templates,
    template?.templateName,
    existingDiskSize,
    requirePassword,
    user?.is_power_user,
    saveTemplate,
    isCreateTemplate
  );
  const schema = Yup.object().shape(validation, [
    ["newDiskName", "existingDiskName"],
    ["newDiskSize", "existingDiskSize"],
  ]);

  const findDiskIndex = (volumeId: string): number => {
    return disks?.findIndex((disk) => disk.id === volumeId);
  };

  const maxQueueSize = myOrg?.data.max_cluster_size || blueprint?.max_queue_size; // Org value of max queue size takes priority
  const initialQueueSize = blueprint?.initial_queue_size || 0;
  const workflowName = template?.body?.workflowName || "";
  const tagsDefault = template?.body?.tags;
  const diskIndex = findDiskIndex(template?.body?.volumeId);
  const diskInfo: TemplateDiskInfo = {
    id: template?.body?.volumeId || "",
    name: diskIndex >= 0 ? disks[diskIndex].name : "",
    size: diskIndex >= 0 ? disks[diskIndex].size : null,
    isEditForm: false,
  };
  const existingDiskNameDefault = diskInfo.name;
  const existingDiskSizeDefault = diskInfo.size;

  return (
    <Form<CreateFormInput, typeof schema>
      noValidate // Disable native HTML validation and use Yup for entire form
      id="create-workflow"
      onSubmit={submit}
      autoComplete={"off"}
      options={{
        context: {
          usePublicIPs: myOrg?.data.use_public_ips,
          newDiskDisabled: newDiskDisabled,
          existingDiskDisabled: existingDiskDisabled,
        },
        defaultValues: {
          name: workflowName,
          workflow_owner_username: workflowUsername,
          max_queue_size: maxQueueSize,
          initial_queue_size: initialQueueSize,
          access_from: "0.0.0.0/0",
          tags: tagsDefault,
          existingDiskName: existingDiskNameDefault,
          existingDiskSize: existingDiskSizeDefault,
        },
      }}
      schema={schema}
    >
      {({ register, formState, getValues, setValue, resetField, setError, clearErrors, control, watch }) => {
        return (
          <>
            <Box>
              <SubHeading>
                <Typography color="textPrimary" variant="h5">
                  <FontAwesomeIconWithTheme icon={faCloud} />
                  Workflow Information
                </Typography>
                <Typography color="textSecondary" variant="body1">
                  Enter information for your workflow
                </Typography>
              </SubHeading>

              <BasicInfoInput
                register={register}
                clearErrors={clearErrors}
                formState={formState}
                setValue={setValue}
                template={template}
                blueprints={blueprints}
                watch={watch}
                setBlueprintId={setBlueprintId}
                blueprintName={template?.blueprintName || blueprint.name}
                source={source || "launch-workflow"}
              />
              {!isCreateTemplate && (
                <CredentialsInput register={register} formState={formState} requirePassword={requirePassword} />
              )}
              <NodeInput
                resetField={resetField}
                control={control}
                params={{
                  head_node_instance_types: nodeData?.head_node_instance_types,
                  compute_node_instance_types: nodeData?.compute_node_instance_types,
                  gpu_head_node_instance_types: nodeData?.gpu_head_node_instance_types,
                  gpu_compute_node_instance_types: nodeData?.gpu_compute_node_instance_types,
                  template: template,
                }}
                gpuInitial={template?.body.isGPU || false}
                register={register}
              />
              {isCreateTemplate ? (
                blueprintId && (
                  <SchedulerInput
                    setUserSchedulersError={setUserSchedulersError}
                    setBlueprintSchedulersError={setBlueprintSchedulersError}
                    template={null}
                    blueprintId={blueprintId}
                    formState={formState}
                    register={register}
                    setInitializeExpanded={null}
                  />
                )
              ) : (
                <AdvancedSettings
                  params={{
                    head_node_instance_types: nodeData?.head_node_instance_types,
                    compute_node_instance_types: nodeData?.compute_node_instance_types,
                    max_queue_size: maxQueueSize,
                    maintain_initial_size: blueprint?.maintain_initial_size,
                    use_public_ips: myOrg?.data.use_public_ips,
                    template: template,
                  }}
                  actions={{
                    maintain_initial_size_allowed: blueprintActions.maintain_initial_size_enabled,
                  }}
                  powerUser={user?.is_power_user}
                  register={register}
                  formState={formState}
                  setValue={setValue}
                  setError={setError}
                  clearErrors={clearErrors}
                  blueprintId={blueprintId}
                  setUserSchedulersError={setUserSchedulersError}
                  setBlueprintSchedulersError={setBlueprintSchedulersError}
                />
              )}

              <Divider
                sx={{
                  margin: "20px 10px",
                }}
              />
              {!isCreateTemplate && (
                <>
                  <SubHeading>
                    <Typography color="textPrimary" variant="h5">
                      <FontAwesomeIconWithTheme icon={faHdd} />
                      Disk Information
                    </Typography>

                    <Typography color="textSecondary" variant="body1">
                      {availableDisks
                        ? "Complete one of the following disk selections"
                        : "Create a new disk to use with this workflow"}
                    </Typography>
                  </SubHeading>
                  <DiskInfoInput
                    register={register}
                    formState={formState}
                    clearErrors={clearErrors}
                    availableDisks={availableDisks}
                    availableDisksFetchError={disksError}
                    inUseDisks={[]}
                    diskInfo={diskInfo}
                    setValue={setValue}
                    getValues={getValues}
                    control={control}
                    disabled={{
                      newDiskDisabled,
                      existingDiskDisabled,
                      setNewDiskDisabled,
                      setExistingDiskDisabled,
                    }}
                    setExistingDiskSize={setExistingDiskSize}
                  />
                  <Divider
                    sx={{
                      margin: "20px 10px",
                    }}
                  />
                </>
              )}
              <ExpandableSettings
                label="Show tag settings"
                initializeAsExpanded={false}
                errors={formState.errors?.tags}
              >
                <TagsInput register={register} formState={formState} />
              </ExpandableSettings>

              {source === "launch-workflow" && (
                <TemplateConfig
                  register={register}
                  formState={formState}
                  saveTemplate={saveTemplate}
                  setSaveTemplate={setSaveTemplate}
                  saveDisk={saveDisk}
                  setSaveDisk={setSaveDisk}
                />
              )}

              {isCreateTemplate && (
                <>
                  <GroupInput
                    formState={formState}
                    templateGroups={undefined}
                    groups={groups}
                    setValue={setValue}
                  ></GroupInput>
                  <TemplateNameInput register={register} formState={formState} />
                </>
              )}
            </Box>

            <ButtonGroup>
              <Button size="small" disabled={submitting} variant="contained" onClick={close} sx={{ margin: "5px" }}>
                Cancel
              </Button>

              <Button type="submit" disabled={submitting} size="small" variant="contained" sx={{ margin: "5px" }}>
                {launchText}
              </Button>
              <div className="small-loader" role="loader">
                {submitting && <Loader />}
              </div>
            </ButtonGroup>
          </>
        );
      }}
    </Form>
  );
};
